import { Component, OnInit, OnDestroy, Inject } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { IMicroSprinkler, IMicroSprinklerMeta } from '../../../models/micro-sprinkler/interfaces';
import { IMicroSprinklerSettingViewModel } from '../interfaces';
import { MicroSprinklerFields } from '../calculatorSettingsForm';
import { MicroSprinklerService } from '../../../models/micro-sprinkler/service';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { SharedUpdateService } from '../../shared/dialogs/update.service';
import { FormValidation } from '../../../classes/formValidation';

export interface IMicroSprinklerFilterGroup {
	Manufacturer: IMicroSprinkler[],
	Model: IMicroSprinkler[],
	Series: IMicroSprinkler[],
	Color: IMicroSprinkler[],
	Pressure: IMicroSprinkler[]
}

@Component({
	moduleId: module.id,
	selector: 'micro-sprinkler',
	templateUrl: 'micro-sprinkler.html'
})

export class MicroSprinklerComponent implements OnInit, OnDestroy {

	public form: FormGroup;

	/// used to display a more robust dropdown
	public microSprinklersFilteredGroup: IMicroSprinklerFilterGroup = {
		Manufacturer: null,
		Model: null,
		Series: null,
		Color: null,
		Pressure: null
	}

	private _control: AbstractControl;
	private microSprinklers: IMicroSprinkler[]; // database of all micro sprinklers
		// used to populate dropdowns

	public manufacturers: IMicroSprinklerMeta[];
	public models: IMicroSprinklerMeta[];
	public seriesList: IMicroSprinklerMeta[];
	public colors: IMicroSprinklerMeta[];
	public pressures: IMicroSprinklerMeta[];

	private PROPERTIES = {
		MANUFACTURER: 'Manufacturer',
		SERIES: 'Series',
		MODEL: 'Model',
		COLOR: 'Color',
		PRESSURE: 'Pressure'
	};

	private _subscriptions$: Subject<boolean>;
	private _initialValues: IMicroSprinklerSettingViewModel;

	public get f(): { [key in MicroSprinklerFields]: AbstractControl } {
		return this.form.controls as { [key in MicroSprinklerFields]: AbstractControl };
	}

	constructor(
		@Inject(MAT_DIALOG_DATA) private _data: {
			form: FormGroup, control: AbstractControl },

		private _dialogRef: MatDialogRef<MicroSprinklerComponent>,
		private microSprinklerService: MicroSprinklerService,
		private _sharedUpdateService: SharedUpdateService) {
	}

	ngOnInit(): void {
		this.form = this._data.form;
		this._control = this._data.control;
		this._subscriptions$ = new Subject();
		this._initialValues = this.form.value;
		this._bindValueChanges();

		this.microSprinklerService.list().then(response => {
			if (response) {
				this.microSprinklers = response;
			}

			// setup default values - reduces need to validate

			this.filterDropdowns();
			this._initializeMicroSprinklerModel();
		});
	}

	ngOnDestroy(): void {
		if (!this._subscriptions$) {
			return;
		}

		this._subscriptions$.next(true);
		this._subscriptions$.complete();
	}

	public calculate(e: MouseEvent): void {
		let result: number;

		if (this.f.UseFlowRate.value) {
			result = MicroSprinklerService.calculate(this.f.FlowRate.value,
				this.f.InRowTreeSpacing.value, this.f.BetweenRowTreeSpacing.value,
				this.f.MicroSprinklersPerTree.value);
		} else {
			let microSprinkler: IMicroSprinkler;

			microSprinkler = this._findMicroSprinkler(this.microSprinklers, this.f.ManufacturerId.value,
				this.f.SeriesId.value, this.f.ColorId.value, this.f.PressureName.value);

			if (!microSprinkler) {
				return;
			}

			result = MicroSprinklerService.calculate(microSprinkler.ApplicationRate,
				this.f.InRowTreeSpacing.value, this.f.BetweenRowTreeSpacing.value,
				this.f.MicroSprinklersPerTree.value);

			this.f.MicroSprinklerId.setValue(microSprinkler.Id);
		}

		result = Number(result.toFixed(2));
		this._control.setValue(result);
		this._closeDialog();
	}

	public cancel(e: MouseEvent): void {
		this.form.reset(this._initialValues);
		this._clearValidators();
		this._closeDialog();
	}

	private _clearValidators(): void {
		FormValidation.clearValidator(this.f.BetweenRowTreeSpacing);
		FormValidation.clearValidator(this.f.ColorId);
		FormValidation.clearValidator(this.f.FlowRate);
		FormValidation.clearValidator(this.f.InRowTreeSpacing);
		FormValidation.clearValidator(this.f.ManufacturerId);
		FormValidation.clearValidator(this.f.MicroSprinklerId);
		FormValidation.clearValidator(this.f.MicroSprinklersPerTree);
		FormValidation.clearValidator(this.f.ModelId);
		FormValidation.clearValidator(this.f.PressureName);
		FormValidation.clearValidator(this.f.SeriesId);
		FormValidation.clearValidator(this.f.UseFlowRate);
		FormValidation.clearValidator(this.form);
	}

	public filterDropdowns(): void {
		this.microSprinklersFilteredGroup.Manufacturer = this._filterDropdown(this.PROPERTIES.MANUFACTURER, this.microSprinklers);
		this.microSprinklersFilteredGroup.Series = this._filterDropdown(this.PROPERTIES.SERIES, this.microSprinklers);
		this.microSprinklersFilteredGroup.Color = this._filterDropdown(this.PROPERTIES.COLOR, this.microSprinklers);
		this.microSprinklersFilteredGroup.Pressure = this._filterDropdown(this.PROPERTIES.PRESSURE, this.microSprinklers);

		this._createDropdowns(this.microSprinklersFilteredGroup);
	}

	public reset(): void {
		this.f.ManufacturerId.setValue(null);
		this.f.SeriesId.setValue(null);
		this.f.ColorId.setValue(null);
		this.f.PressureName.setValue(null);
		this.filterDropdowns();
	}

	private _closeDialog() {
		this._sharedUpdateService.cancel();
		this._dialogRef.close();
	}

	/**
     * Redraw dropdowns when user chooses a dropdown option
     * @param group
     */
	private _createDropdowns(group: IMicroSprinklerFilterGroup): void {
		let colorsUnsorted: IMicroSprinklerMeta[];

		this.manufacturers = this._createMetaArray(group.Manufacturer, this.PROPERTIES.MANUFACTURER);
		this.seriesList = this._createMetaArray(group.Series, this.PROPERTIES.SERIES);
		colorsUnsorted = this._createMetaArray(group.Color, this.PROPERTIES.COLOR);

		if (colorsUnsorted && colorsUnsorted.length > 0) {
			this.colors = colorsUnsorted.sort((a, b) => {
				return (a.Name < b.Name) ? -1 : (a.Name > b.Name) ? 1 : 0;
			});
		}

		this.pressures = this._createMetaArray(group.Pressure, this.PROPERTIES.PRESSURE)
	}

	/**
     * Create dropdown array
     * @param microSprinklers
     * @param fieldName
     */
	private _createMetaArray(microSprinklers: IMicroSprinkler[], fieldName: string): IMicroSprinklerMeta[] {

		let result: IMicroSprinklerMeta[];
		let reverseMap: Map<string, string>;

		result = new Array();
		reverseMap = new Map<string, string>();

		if (!microSprinklers || microSprinklers.length === 0) {
			return null;
		}

		for (let microSprinkler of microSprinklers) {

			switch (fieldName) {
				case this.PROPERTIES.MANUFACTURER:
					if (!reverseMap.get(microSprinkler.Manufacturer.Id.toString())) {
						reverseMap.set(microSprinkler.Manufacturer.Id.toString(), microSprinkler.Manufacturer.Name);
						result.push(microSprinkler.Manufacturer);
					}
					break;
				case this.PROPERTIES.SERIES:
					if (!reverseMap.get(microSprinkler.Series.Id.toString())) {
						reverseMap.set(microSprinkler.Series.Id.toString(), microSprinkler.Series.Name);
						result.push(microSprinkler.Series);
					}

					break;
				case this.PROPERTIES.COLOR:
					if (!reverseMap.get(microSprinkler.Color.Id.toString())) {
						reverseMap.set(microSprinkler.Color.Id.toString(), microSprinkler.Color.Name);
						result.push(microSprinkler.Color);
					}

					break;
				case this.PROPERTIES.PRESSURE:
					if (!reverseMap.get((<IMicroSprinklerMeta> microSprinkler.Pressure).Name)) {
						reverseMap.set(microSprinkler.Pressure.Name, microSprinkler.Pressure.Name);
						result.push(microSprinkler.Pressure);
					}

					break;
			}
		}

		return result;
	}

	private _filterDropdown(fieldName: string, microSprinklers: IMicroSprinkler[]): IMicroSprinkler[] {
		let result: IMicroSprinkler[];
		let manufacturerId: number;
		let colorId: number;
		let seriesId: number;
		let modelId: number;
		let pressureName: string;

		result = microSprinklers;

		manufacturerId = this.f.ManufacturerId.value;
		modelId = this.f.ModelId.value;
		colorId = this.f.ColorId.value;
		seriesId = this.f.SeriesId.value;
		pressureName = this.f.PressureName.value;

		switch (fieldName) {
			case this.PROPERTIES.MANUFACTURER:
				result = seriesId ? result.filter(x => x.Series.Id === seriesId) : result;
				result = colorId ? result.filter(x => x.Color.Id === colorId) : result;
				result = pressureName ? result.filter(x => x.Pressure.Name === pressureName) : result;
				break;
			case this.PROPERTIES.SERIES:
				result = manufacturerId ? result.filter(x => x.Manufacturer.Id === manufacturerId) : result;
				result = colorId ? result.filter(x => x.Color.Id === colorId) : result;
				result = pressureName ? result.filter(x => x.Pressure.Name === pressureName) : result;
				break;
			case this.PROPERTIES.COLOR:
				result = manufacturerId ? result.filter(x => x.Manufacturer.Id === manufacturerId) : result;
				result = seriesId ? result.filter(x => x.Series.Id === seriesId) : result;
				result = pressureName ? result.filter(x => x.Pressure.Name === pressureName) : result;
				break;
			case this.PROPERTIES.PRESSURE:
				result = manufacturerId ? result.filter(x => x.Manufacturer.Id === manufacturerId) : result;
				result = seriesId ? result.filter(x => x.Series.Id === seriesId) : result;
				result = colorId ? result.filter(x => x.Color.Id === colorId) : result;
				break;
		}

		return result;
	}

	private _findMicroSprinkler(microSprinklers: IMicroSprinkler[], manufacturerId: number,
		seriesId: number, colorId: number,
		pressure: string): IMicroSprinkler {

		let result: IMicroSprinkler;

		if (!microSprinklers || !manufacturerId || !seriesId || !colorId || !pressure) {
			return null;
		}

		result = microSprinklers.filter(x => x.Pressure.Name === pressure && x.Manufacturer.Id ===
			manufacturerId && x.Series.Id === seriesId && x.Color.Id === colorId)[0];

		return result;
	}

	private _bindValueChanges(): void {
		this.f.ManufacturerId.valueChanges.pipe(takeUntil(this._subscriptions$)).subscribe(() => {
			this.filterDropdowns();
		});

		this.f.ModelId.valueChanges.pipe(takeUntil(this._subscriptions$)).subscribe(() => {
			this.filterDropdowns();
		});

		this.f.ColorId.valueChanges.pipe(takeUntil(this._subscriptions$)).subscribe(() => {
			this.filterDropdowns();
		});

		this.f.PressureName.valueChanges.pipe(takeUntil(this._subscriptions$)).subscribe(() => {
			this.filterDropdowns();
		});

		this.f.SeriesId.valueChanges.pipe(takeUntil(this._subscriptions$)).subscribe(() => {
			this.filterDropdowns();
		});
	}

	/**
	 * Initialize form fields specific to configuring a specific microsprinkler,
	 */
	private _initializeMicroSprinklerModel(): void {
		let microSprinkler: IMicroSprinkler;

		if (!this.form) {
			throw new Error('form is empty');
		}

		if (!this.f.MicroSprinklerId.value) { // this is unselected by default
			return;
		}

		microSprinkler = this.microSprinklers.find(x => x.Id ===
			this.f.MicroSprinklerId.value);

		if (!microSprinkler) {
			throw new Error('microsprfinkler not found');
		}

		if (this.manufacturers && this.manufacturers.length > 0 && microSprinkler.Manufacturer) {
			this.f.ManufacturerId.setValue(microSprinkler.Manufacturer.Id);
			this.filterDropdowns();
		}

		if (this.seriesList && this.seriesList.length > 0 && microSprinkler.Series) {
			this.f.SeriesId.setValue(microSprinkler.Series.Id);
			this.filterDropdowns();
		}

		if (this.colors && this.colors.length > 0 && microSprinkler.Color) {
			this.f.ColorId.setValue(microSprinkler.Color.Id);
			this.filterDropdowns();
		}

		if (this.pressures && this.pressures.length > 0 && microSprinkler.Pressure) {
			this.f.PressureName.setValue(microSprinkler.Pressure.Name);
			this.filterDropdowns();
		}
	}
}
